import React, { useEffect, useRef } from 'react';
import { useTexture } from '../../hooks/media/useTexture';
import { Project } from '../../types';
import { Mesh, PlaneGeometry, ShaderMaterial, Vector3 } from 'three';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { useAppStore } from '../../store/useAppStore';

// ⚡ MEMORY: Pre-allocated vectors for getProjectedRect - never create in render/handlers
const _corners = [
  new Vector3(),
  new Vector3(),
  new Vector3(),
  new Vector3(),
];
const _projectedPoint = new Vector3();

interface GalleryItemProps {
  project: Project;
  index: number;
  originalIndex: number;
  totalProjects: number;
  material: ShaderMaterial;
  imageScale: [number, number];
  sharedGeometry: PlaneGeometry;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: (isHovered: boolean) => void;
}

const GalleryItemComponent: React.FC<GalleryItemProps> = ({
  project,
  originalIndex,
  material,
  imageScale,
  sharedGeometry,
  onSelect,
  onHover,
  isHovered,
  isSelected,
  totalProjects,
}) => {
  const meshRef = useRef<Mesh>(null);
  const [width, height] = useTexture(material, project.thumbnail);
  const { camera, size } = useThree();

  // Calculer le ratio et les dimensions finales
  const aspectRatio = width / height;
  const finalHeight = imageScale[1];
  // Ajuster la largeur en conservant le ratio d'aspect tout en tenant compte de la taille de l'écran
  const finalWidth = finalHeight * aspectRatio;

  // Limiter la taille maximale sur les petits écrans
  const maxWidth = window.innerWidth * 0.8; // 80% de la largeur de l'écran
  const maxHeight = window.innerHeight * 0.8; // 80% de la hauteur de l'écran

  const scaleFactor = Math.min(
    maxWidth / finalWidth,
    maxHeight / finalHeight,
    1
  );

  const adjustedWidth = finalWidth * scaleFactor;
  const adjustedHeight = finalHeight * scaleFactor;

  useEffect(() => {
    if (isSelected) {
      onHover(false);
      useAppStore.getState().setHoveringProject(false);
    }

    return () => {
      useAppStore.getState().setHoveringProject(false);
    };
  }, [isSelected, onHover]);

  const handlePointerEvents = {
    onClick: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        useAppStore.getState().setTransitionOrigin(getProjectedRect());
        onSelect();
      }
    },
    onPointerOver: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        onHover(true);
        useAppStore.getState().setHoveringProject(true);
      }
    },
    onPointerOut: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        onHover(false);
        useAppStore.getState().setHoveringProject(false);
      }
    },
  };

  const getDisplayIndex = (originalIndex: number, totalProjects: number) => {
    return totalProjects - originalIndex;
  };

  const getProjectedRect = () => {
    const mesh = meshRef.current;
    if (!mesh) return null;

    // sharedGeometry is PlaneGeometry(1,1) → corners in local space at ±0.5
    const half = 0.5;
    _corners[0].set(-half, -half, 0);
    _corners[1].set(half, -half, 0);
    _corners[2].set(half, half, 0);
    _corners[3].set(-half, half, 0);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < 4; i++) {
      mesh.localToWorld(_projectedPoint.copy(_corners[i]));
      _projectedPoint.project(camera);
      const x = ((_projectedPoint.x + 1) / 2) * size.width;
      const y = ((-_projectedPoint.y + 1) / 2) * size.height;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    const left = minX;
    const top = minY;
    const rectWidth = maxX - minX;
    const rectHeight = maxY - minY;

    return {
      left,
      top,
      width: rectWidth,
      height: rectHeight,
      borderRadius: project.type === 'mobile' ? 28 : 24,
    };
  };

  return (
    <group {...handlePointerEvents}>
      <mesh ref={meshRef} scale={[adjustedWidth, adjustedHeight, 1]}>
        <primitive object={sharedGeometry} attach="geometry" dispose={null} />
        <primitive object={material} attach="material" />
      </mesh>
      {(isHovered || window.innerWidth < 1024) && !isSelected && (
        <group rotation={meshRef.current?.rotation ?? [0, 0, 0]}>
          <group scale={0.5}>
            <Html
              position={[0, -adjustedHeight / 2 - 1.75, -0.01]}
              center
              className="pointer-events-none"
              transform
              rotation={[0, Math.PI, 0]}
              occlude
              zIndexRange={[0, 100]}
              renderOrder={-1}
            >
              <div className="project-label flex min-h-[40px] w-[300px] items-center justify-between rounded-xl bg-white/[0.78] px-4 py-2">
                <span className="text-[0.95rem] font-light text-black/85">
                  {project.title}
                </span>
                <span className="label text-black/62">
                  {getDisplayIndex(originalIndex, totalProjects)}/{totalProjects}
                </span>
              </div>
            </Html>
          </group>
        </group>
      )}
    </group>
  );
};

export const GalleryItem = React.memo(GalleryItemComponent);
